/**
 * CORE-467 — FE mirror of the backend Error Codes registry
 * (agape-backend/src/Agape.Shared/Models/ErrorCodes.cs).
 *
 * The string values are the wire contract — keep them in lock-step with the
 * backend. This module is the single place the FE maps a machine-readable
 * `code` (from a REST `{ code }` body or a SignalR `Error` payload) to
 * canonical Russian user copy. Previously these were scattered inline string
 * checks (GuestRoomScreen, MasterLivePublisher) and `hubError` just echoed the
 * server's English `message`.
 */
export const ERROR_CODES = {
  // Room / party access
  NotParticipant: 'not_participant',
  NotHost: 'not_host',
  /**
   * CORE-1840 — сервер отказал, потому что сессия не открыта. Хаб отвечает им
   * и на `JoinParty` в уже завершённую комнату, и на любой `InvalidOperation`
   * в обработчиках, поэтому сам по себе конец не доказывает: экран
   * перечитывает комнату и верит её статусу.
   */
  PartyEnded: 'party_ended',
  // Cohost (live preview-token join)
  CohostTokenBound: 'cohost_token_bound',
  CohostCapacityFull: 'cohost_capacity_full',
  // Moderation
  Kicked: 'kicked',
  /**
   * CORE-1297/CORE-137 — «вас отсюда исключили». Сессия помнит кик, и вход
   * отвечает им вместо того, чтобы пустить: REST (`/join`, `/spectate`,
   * `/spectate/rtc-token`) — статусом 403, хаб (`JoinParty`) — кадром `Error`.
   * Отличать от `not_participant` обязательно: тот значит «строка закрылась,
   * пока вас не было» и лечится перезаходом, а этот — терминальный.
   */
  KickedFromParty: 'kicked_from_party',
  // Spectator
  InvalidSpectator: 'invalid_spectator',
  SpectatorFull: 'spectator_full',
  // Chat / messaging
  EmptyMessage: 'empty_message',
  MessageTooLong: 'message_too_long',
  RateLimited: 'rate_limited',
  MessageNotAllowed: 'MESSAGE_NOT_ALLOWED',
  // Media / RTC
  SwitchTrackFailed: 'switch_track_failed',
  // Recording
  RecordingNotConfigured: 'recording_not_configured',
  RecordingNoBroadcastWindow: 'recording_no_broadcast_window',
  RecordingStartFailed: 'recording_start_failed',
  // Timing / lifecycle
  TooEarly: 'too_early',
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];

/** Canonical Russian copy per code. Brand voice — calm, no exclamations. */
const MESSAGES: Record<string, string> = {
  [ERROR_CODES.NotParticipant]: 'Вы не участник этой Live-сессии.',
  [ERROR_CODES.NotHost]: 'Управлять сессией может только ведущий.',
  [ERROR_CODES.CohostTokenBound]: 'Эта ссылка уже привязана к другому участнику.',
  [ERROR_CODES.CohostCapacityFull]: 'Все места со-ведущих сейчас заняты.',
  [ERROR_CODES.Kicked]: 'Ведущий удалил вас из Live-сессии.',
  [ERROR_CODES.InvalidSpectator]: 'Не удалось войти как зритель.',
  [ERROR_CODES.SpectatorFull]: 'В Live-сессии достигнут лимит зрителей.',
  [ERROR_CODES.EmptyMessage]: 'Сообщение не может быть пустым.',
  [ERROR_CODES.MessageTooLong]: 'Сообщение слишком длинное.',
  [ERROR_CODES.RateLimited]: 'Слишком часто. Подождите немного и попробуйте снова.',
  [ERROR_CODES.MessageNotAllowed]: 'Это сообщение отправить нельзя.',
  [ERROR_CODES.SwitchTrackFailed]: 'Не удалось переключить устройство. Попробуйте ещё раз.',
  [ERROR_CODES.RecordingNotConfigured]: 'Запись не настроена для этой Live-сессии.',
  [ERROR_CODES.RecordingNoBroadcastWindow]: 'Сейчас нет активной Live-сессии для записи.',
  [ERROR_CODES.RecordingStartFailed]: 'Не удалось начать запись. Попробуйте ещё раз.',
  [ERROR_CODES.TooEarly]: 'Ещё рано — сессия пока не началась.',
};

/**
 * Canonical Russian copy for a known error code, or null if the code isn't in
 * the registry (caller should fall back to its own message / generic copy).
 */
export function errorCodeMessage(code: string | null | undefined): string | null {
  if (!code) return null;
  return MESSAGES[code] ?? null;
}
